import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import {
  ParkingChartDot,
  ParkingDashboardStatCard,
  ParkingFinanceDailyPoint,
  ParkingFinanceDashboardResult,
  ParkingRevenueBar,
} from '../../../../core/models/report/parking-dashboard.model';
import { MapperParkingDashboardService } from '../../../../core/mappers/report/mapper-parking-dashboard.service';
import { ApiParkingDashboardService } from '../../../../core/services/api/report/api-parking-dashboard.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';

type PeriodMode = 'month' | 'range';
type ChartType = 'line' | 'bar';

@Component({
  selector: 'app-dashboard-parking-finance',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent],
  templateUrl: './dashboard-parking-finance.component.html',
  styleUrl: './dashboard-parking-finance.component.css',
})
export class DashboardParkingFinanceComponent {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiParkingDashboardService);
  private readonly mapper = inject(MapperParkingDashboardService);
  private readonly toast = inject(ToastService);

  isLoading = signal(false);
  hasSearched = signal(false);
  lastFetchedTime: Date | null = null;
  result = signal<ParkingFinanceDashboardResult | null>(null);
  cards = signal<ParkingDashboardStatCard[]>([]);

  periodMode: PeriodMode = 'month';
  monthValue = '';
  startDate = '';
  endDate = '';
  revenueChartType: ChartType = 'bar';

  revenueYLabels: number[] = [0, 0, 0, 0, 0];
  revenueLinePoints = '';
  revenueAreaPoints = '';
  revenueDots: ParkingChartDot[] = [];
  revenueBars: ParkingRevenueBar[] = [];
  dayLabels: string[] = [];

  hoveredRevenue = signal<number | null>(null);

  private readonly maxRangeMonths = 2;

  constructor() {
    const now = new Date();
    this.monthValue = this.toInputMonth(now);
    const { start, end } = this.monthToRange(this.monthValue);
    this.startDate = start;
    this.endDate = end;

    effect(() => {
      const siteId = this.siteState.selectedSite()?.id ?? 0;
      untracked(() => {
        this.clearResult();
        if (siteId > 0) {
          this.loadDashboard();
        }
      });
    });
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0 && !!this.startDate && !!this.endDate;
  }

  get periodLabel(): string {
    if (!this.startDate || !this.endDate) return '—';
    return `${this.formatDate(this.startDate)} – ${this.formatDate(this.endDate)}`;
  }

  get maxEndDate(): string | undefined {
    if (!this.startDate) return undefined;
    const start = this.parseInputDate(this.startDate);
    if (!start) return undefined;
    return this.toInputDate(
      new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate()),
    );
  }

  get hasChartData(): boolean {
    return (this.result()?.daily?.length ?? 0) > 0;
  }

  onPeriodModeChange(mode: PeriodMode): void {
    this.periodMode = mode;
    if (mode === 'month' && this.monthValue) {
      const { start, end } = this.monthToRange(this.monthValue);
      this.startDate = start;
      this.endDate = end;
    }
    this.clearResult();
  }

  onMonthChange(): void {
    if (!this.monthValue) return;
    const { start, end } = this.monthToRange(this.monthValue);
    this.startDate = start;
    this.endDate = end;
    this.clearResult();
  }

  onRangeChange(): void {
    if (this.startDate && this.endDate && this.startDate > this.endDate) {
      this.endDate = this.startDate;
    }
    if (this.startDate && this.endDate && this.isRangeOverMax(this.startDate, this.endDate)) {
      const maxEnd = this.maxEndDate;
      if (maxEnd) this.endDate = maxEnd;
      this.toast.warn('เรียกดูได้สูงสุด 2 เดือน — ปรับวันสิ้นสุดให้อัตโนมัติ');
    }
    this.clearResult();
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }
    if (this.startDate > this.endDate) {
      this.toast.error('วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น');
      return;
    }
    if (this.isRangeOverMax(this.startDate, this.endDate)) {
      this.toast.error('เรียกดูได้สูงสุด 2 เดือนเท่านั้น');
      return;
    }
    this.loadDashboard();
  }

  onRefresh(): void {
    if (!this.canSearch) {
      this.onSearch();
      return;
    }
    this.loadDashboard();
  }

  loadDashboard(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api.getParkingFinanceDashboard({
      siteId: this.selectedSiteId,
      startDate: this.startDate,
      endDate: this.endDate,
    }).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapFinance(res);
        this.result.set(mapped);
        this.cards.set(this.mapper.toFinanceStatCards(mapped));
        this.renderCharts(mapped.daily);
        this.hasSearched.set(true);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.result.set(null);
        this.cards.set([]);
        this.clearCharts();
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลด Dashboard ไม่สำเร็จ');
      },
    });
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = this.parseInputDate(value) ?? new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  formatShortDate(value: string | null | undefined): string {
    if (!value) return '—';
    const d = this.parseInputDate(value) ?? new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  formatMoney(value: number): string {
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  private renderCharts(daily: ParkingFinanceDailyPoint[]): void {
    this.dayLabels = daily.map((d) => this.formatShortDate(d.date));

    const revenueValues = daily.map((d) => d.totalRevenue);

    const revenueMax = Math.max(...revenueValues, 100);
    const revenueCeil = Math.ceil(revenueMax / 4) * 4 || 4;
    this.revenueYLabels = [0, revenueCeil * 0.25, revenueCeil * 0.5, revenueCeil * 0.75, revenueCeil];

    const peakRevenueIdx = this.indexOfMax(revenueValues);

    this.revenueBars = daily.map((d, i) => ({
      label: this.dayLabels[i] ?? '',
      fullDate: this.formatDate(d.date),
      value: d.totalRevenue,
      pct: d.totalRevenue <= 0 ? 0 : Math.max(3, (d.totalRevenue / revenueCeil) * 100),
      isPeak: i === peakRevenueIdx && d.totalRevenue > 0,
    }));

    const revenueCoords = this.toLineCoords(revenueValues, revenueCeil);

    this.revenueLinePoints = revenueCoords.map((p) => `${p.x},${p.y}`).join(' ');
    this.revenueAreaPoints = this.toArea(revenueCoords);

    this.revenueDots = revenueCoords.map((p, i) => ({
      x: p.x,
      y: p.y,
      label: this.dayLabels[i] ?? '',
      value: revenueValues[i] ?? 0,
      color: '#f59e0b',
      isPeak: i === peakRevenueIdx && (revenueValues[i] ?? 0) > 0,
    }));
  }

  private indexOfMax(values: number[]): number {
    let max = -1;
    let idx = 0;
    values.forEach((v, i) => {
      if (v > max) {
        max = v;
        idx = i;
      }
    });
    return idx;
  }

  private toLineCoords(values: number[], maxVal: number): { x: number; y: number }[] {
    const padL = 28;
    const padR = 16;
    const padT = 20;
    const padB = 28;
    const w = 640 - padL - padR;
    const h = 240 - padT - padB;
    const total = values.length;
    return values.map((val, index) => {
      const x = total <= 1 ? padL + w / 2 : padL + (index / (total - 1)) * w;
      const y = padT + h - (val / maxVal) * h;
      return { x, y };
    });
  }

  private toArea(points: { x: number; y: number }[]): string {
    if (points.length === 0) return '';
    const baseline = 212;
    const first = points[0];
    const last = points[points.length - 1];
    return `${first.x},${baseline} ${points.map((p) => `${p.x},${p.y}`).join(' ')} ${last.x},${baseline}`;
  }

  private clearCharts(): void {
    this.revenueYLabels = [0, 0, 0, 0, 0];
    this.revenueLinePoints = '';
    this.revenueAreaPoints = '';
    this.revenueDots = [];
    this.revenueBars = [];
    this.dayLabels = [];
    this.hoveredRevenue.set(null);
  }

  private clearResult(): void {
    this.result.set(null);
    this.cards.set([]);
    this.clearCharts();
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  private monthToRange(monthValue: string): { start: string; end: string } {
    const m = /^(\d{4})-(\d{2})$/.exec(monthValue.trim());
    if (!m) {
      const today = this.toInputDate(new Date());
      return { start: today, end: today };
    }
    const year = Number(m[1]);
    const monthIndex = Number(m[2]) - 1;
    const start = new Date(year, monthIndex, 1);
    const end = new Date(year, monthIndex + 1, 0);
    return { start: this.toInputDate(start), end: this.toInputDate(end) };
  }

  private toInputMonth(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private parseInputDate(value: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private isRangeOverMax(startStr: string, endStr: string): boolean {
    const start = this.parseInputDate(startStr);
    const end = this.parseInputDate(endStr);
    if (!start || !end) return false;
    const maxEnd = new Date(start.getFullYear(), start.getMonth() + this.maxRangeMonths, start.getDate());
    return end > maxEnd;
  }
}
